import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Button, Select, ColorPicker, Divider, Space } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  FontColorsOutlined,
  HighlightOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  UndoOutlined,
  RedoOutlined,
  ColumnHeightOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const FONT_SIZES = ["12px", "14px", "16px", "18px", "24px", "32px"];
// 1.3 is the default (matches the CSS base line-height); all numeric so the
// dropdown width stays even.
const LINE_HEIGHTS = [
  { value: "0.75", label: "0.75" },
  { value: "1", label: "1.0" },
  { value: "1.15", label: "1.15" },
  { value: "1.3", label: "1.3" },
  { value: "1.5", label: "1.5" },
  { value: "2", label: "2.0" },
];
const DEFAULT_COLOR = "#1a1a1a";
const DEFAULT_HIGHLIGHT = "#ffe58f";

// Font-color quick-select = the team's 5 colors (from Redux) followed by these
// 3 fixed extras (see textColors below). Highlight keeps its own static swatches.
const EXTRA_TEXT_COLORS = ["#d9534f", "#FFC83D", "#31B452"];
const HIGHLIGHT_COLORS = ["#ffe58f", "#fff1b8", "#b7eb8f", "#91d5ff", "#ffadd2", "#ffd591", "#d9d9d9"];

const preventBlur = (e: React.MouseEvent) => e.preventDefault();
const active = (on?: boolean) => (on ? "is-active" : undefined);

// Custom color-picker panel: a single row of quick-select swatches (with an
// optional "none" swatch) above antd's gradient picker, so transparent/none
// reads as part of the swatch row instead of a separate clear control.
function colorPanel(
  swatches: string[],
  onPick: (color: string) => void,
  Picker: React.ComponentType,
  onNone?: () => void,
) {
  return (
    <div className="rte-color-panel">
      <div className="rte-swatch-row">
        {swatches.map((c) => (
          <button
            key={c}
            type="button"
            className="rte-swatch"
            style={{ background: c }}
            title={c}
            onMouseDown={preventBlur}
            onClick={() => onPick(c)}
          />
        ))}
        {onNone && (
          <button
            type="button"
            className="rte-swatch rte-swatch-none"
            title="No highlight"
            onMouseDown={preventBlur}
            onClick={onNone}
          />
        )}
      </div>
      <Picker />
    </div>
  );
}

// Subscribes to editor state so active marks / sizes / colors / alignment and
// undo-redo availability stay in sync with the selection (v3 toolbars need
// useEditorState for this, not bare isActive/getAttributes/can calls).
export function EditorToolbar({ editor }: { editor: Editor | null }) {
  const state = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor?.isActive("bold") ?? false,
      isItalic: editor?.isActive("italic") ?? false,
      isUnderline: editor?.isActive("underline") ?? false,
      fontSize: (editor?.getAttributes("textStyle").fontSize as string) ?? "",
      color: (editor?.getAttributes("textStyle").color as string) ?? DEFAULT_COLOR,
      highlight: (editor?.getAttributes("highlight").color as string) ?? DEFAULT_HIGHLIGHT,
      alignLeft: editor?.isActive({ textAlign: "left" }) ?? false,
      alignCenter: editor?.isActive({ textAlign: "center" }) ?? false,
      alignRight: editor?.isActive({ textAlign: "right" }) ?? false,
      lineHeight: (editor?.getAttributes("paragraph").lineHeight ||
        editor?.getAttributes("heading").lineHeight ||
        "") as string,
      canUndo: editor?.can().undo() ?? false,
      canRedo: editor?.can().redo() ?? false,
    }),
  });

  const teamColors = useSelector(
    ({ team }: any) => team?.info?.team_colors?.all,
  ) as string[] | undefined;
  const textColors = useMemo(
    () => [...(teamColors ?? []).slice(0, 5), ...EXTRA_TEXT_COLORS],
    [teamColors],
  );

  if (!editor) return null;

  const divider = <Divider type="vertical" className="rte-divider" />;

  return (
    <Space size={1} wrap className="rte-toolbar">
      <Button
        size="small"
        type="text"
        icon={<UndoOutlined />}
        title="Undo"
        disabled={!state?.canUndo}
        onMouseDown={preventBlur}
        onClick={() => editor.chain().focus().undo().run()}
      />
      <Button
        size="small"
        type="text"
        icon={<RedoOutlined />}
        title="Redo"
        disabled={!state?.canRedo}
        onMouseDown={preventBlur}
        onClick={() => editor.chain().focus().redo().run()}
      />
      {divider}
      <Button
        size="small"
        type="text"
        className={active(state?.isBold)}
        icon={<BoldOutlined />}
        title="Bold"
        onMouseDown={preventBlur}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <Button
        size="small"
        type="text"
        className={active(state?.isItalic)}
        icon={<ItalicOutlined />}
        title="Italic"
        onMouseDown={preventBlur}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <Button
        size="small"
        type="text"
        className={active(state?.isUnderline)}
        icon={<UnderlineOutlined />}
        title="Underline"
        onMouseDown={preventBlur}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      {divider}
      <Select
        size="small"
        style={{ width: 58 }}
        title="Font size"
        variant="borderless"
        value={state?.fontSize || "14px"}
        options={FONT_SIZES.map((s) => ({ value: s, label: s.replace("px", "") }))}
        onChange={(val) => editor.chain().focus().setFontSize(val).run()}
      />
      <ColorPicker
        value={state?.color}
        rootClassName="rte-color-popup"
        disabledAlpha
        disabledFormat
        onChangeComplete={(c) => editor.chain().focus().setColor(c.toHexString()).run()}
        panelRender={(_panel, { components: { Picker } }) =>
          colorPanel(textColors, (c) => editor.chain().focus().setColor(c).run(), Picker)
        }
      >
        <Button size="small" type="text" icon={<FontColorsOutlined />} title="Text color" onMouseDown={preventBlur} />
      </ColorPicker>
      <ColorPicker
        value={state?.highlight}
        rootClassName="rte-color-popup"
        disabledAlpha
        disabledFormat
        onChangeComplete={(c) => editor.chain().focus().setHighlight({ color: c.toHexString() }).run()}
        panelRender={(_panel, { components: { Picker } }) =>
          colorPanel(
            HIGHLIGHT_COLORS,
            (c) => editor.chain().focus().setHighlight({ color: c }).run(),
            Picker,
            () => editor.chain().focus().unsetHighlight().run(),
          )
        }
      >
        <Button size="small" type="text" icon={<HighlightOutlined />} title="Highlight" onMouseDown={preventBlur} />
      </ColorPicker>
      {divider}
      <Button
        size="small"
        type="text"
        className={active(state?.alignLeft)}
        icon={<AlignLeftOutlined />}
        title="Align left"
        onMouseDown={preventBlur}
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
      />
      <Button
        size="small"
        type="text"
        className={active(state?.alignCenter)}
        icon={<AlignCenterOutlined />}
        title="Align center"
        onMouseDown={preventBlur}
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
      />
      <Button
        size="small"
        type="text"
        className={active(state?.alignRight)}
        icon={<AlignRightOutlined />}
        title="Align right"
        onMouseDown={preventBlur}
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
      />
      {divider}
      <Select
        size="small"
        style={{ width: 76 }}
        title="Line spacing"
        variant="borderless"
        prefix={<ColumnHeightOutlined />}
        value={state?.lineHeight || "1.3"}
        options={LINE_HEIGHTS}
        onChange={(val) => editor.chain().focus().setLineHeight(val).run()}
      />
      {divider}
      <Button
        size="small"
        type="text"
        icon={<DeleteOutlined />}
        title="Clear all"
        onMouseDown={preventBlur}
        onClick={() => editor.chain().focus().clearContent(true).run()}
      />
    </Space>
  );
}
